import classNames from "classnames"
import React from "react"

// Shared building blocks and formatters for the stats dashboard. Everything the
// tabs render is assembled from these, so a tile on the Economy tab and a tile
// on the Board tab can't drift apart visually.

// --- formatters -------------------------------------------------------------

// Money on this board runs from a few hundred to the tens of millions across a
// career, so the totals are abbreviated rather than printed in full — an exact
// $12,481,300 tells a player less at a glance than $12.5M.
export const formatMoney = (value: number) => {
  const amount = Math.round(value || 0)
  const sign = amount < 0 ? "-" : ""
  const size = Math.abs(amount)
  if (size >= 1_000_000) return `${sign}$${(size / 1_000_000).toFixed(1)}M`
  if (size >= 10_000) return `${sign}$${(size / 1000).toFixed(1)}K`
  return `${sign}$${size.toLocaleString()}`
}

export const formatNumber = (value: number) => {
  const amount = Math.round(value || 0)
  if (Math.abs(amount) >= 1_000_000)
    return `${(amount / 1_000_000).toFixed(1)}M`
  if (Math.abs(amount) >= 10_000) return `${(amount / 1000).toFixed(1)}K`
  return amount.toLocaleString()
}

export const formatPercent = (value: number, digits = 1) =>
  `${(value || 0).toFixed(digits)}%`

export const formatDecimal = (value: number, digits = 1) =>
  (value || 0).toFixed(digits)

export const formatDate = (value?: string) => {
  if (!value) return "—"
  const date = new Date(value)
  if (isNaN(date.getTime())) return "—"
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Keeps a derived 0–100 axis inside its bounds when a player's per-game average
// runs past whatever cap the axis was scaled to.
export const clampScore = (value: number) =>
  Math.max(0, Math.min(100, Math.round(value)))

// --- rank tiers -------------------------------------------------------------

export interface IRankTier {
  name: string
  min: number
  max: number
  color: string
  glow: string
  icon: string
}

// The ladder a rating maps onto. Bands are 150 points wide so a good run of
// games visibly moves a player up rather than nudging a number nobody reads.
export const rankTiers: IRankTier[] = [
  {
    name: "Street Hustler",
    min: 0,
    max: 899,
    color: "#94A3B8",
    glow: "rgba(148,163,184,0.35)",
    icon: "fa-shoe-prints",
  },
  {
    name: "Broker",
    min: 900,
    max: 1049,
    color: "#34D399",
    glow: "rgba(52,211,153,0.35)",
    icon: "fa-handshake",
  },
  {
    name: "Landlord",
    min: 1050,
    max: 1199,
    color: "#38BDF8",
    glow: "rgba(56,189,248,0.35)",
    icon: "fa-house-chimney",
  },
  {
    name: "Tycoon",
    min: 1200,
    max: 1349,
    color: "#A78BFA",
    glow: "rgba(167,139,250,0.35)",
    icon: "fa-city",
  },
  {
    name: "Magnate",
    min: 1350,
    max: 1499,
    color: "#FBBF24",
    glow: "rgba(251,191,36,0.4)",
    icon: "fa-crown",
  },
  {
    name: "Dealmaster",
    min: 1500,
    max: Infinity,
    color: "#F87171",
    glow: "rgba(248,113,113,0.45)",
    icon: "fa-gem",
  },
]

export const getRankTier = (rating: number) =>
  rankTiers.find((tier) => rating >= tier.min && rating <= tier.max) ??
  rankTiers[0]

export const getNextTier = (rating: number) =>
  rankTiers.find((tier) => tier.min > rating)

// --- primitives -------------------------------------------------------------

export const Panel: React.FC<{
  title?: string
  subtitle?: string
  icon?: string
  accent?: string
  className?: string
  children: React.ReactNode
}> = ({ title, subtitle, icon, accent = "#FBBF24", className, children }) => (
  <div
    className={classNames(
      "rounded-2xl border border-white/10 bg-black/40 backdrop-blur-sm p-5 sm:p-3 mobile:p-[12px]",
      className
    )}
  >
    {title && (
      <div className="flex items-center gap-2.5 mb-4 sm:mb-2.5 mobile:gap-[8px] mobile:mb-[10px]">
        {icon && (
          <span
            className="flex h-8 w-8 mobile:h-[26px] mobile:w-[26px] shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${accent}22`, color: accent }}
          >
            <i className={`fas ${icon} text-sm mobile:text-[12px]/[1.4]`} />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="font-fourth mobile:text-[15px]/[1.25] font-bold text-white tracking-wide leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-xs mobile:text-[11px]/[1.25] text-white/40 leading-tight">{subtitle}</p>
          )}
        </div>
      </div>
    )}
    {children}
  </div>
)

export const StatTile: React.FC<{
  label: string
  value: string
  sub?: string
  icon?: string
  accent?: string
}> = ({ label, value, sub, icon, accent = "#FBBF24" }) => (
  <div
    className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-4 sm:p-3 mobile:p-[12px] transition-colors hover:border-white/20"
    style={{ boxShadow: `inset 0 -40px 60px -50px ${accent}` }}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-[0.65rem] mobile:text-[10px]/[1.4] font-semibold uppercase tracking-wider text-white/40">
        {label}
      </p>
      {icon && (
        <i className={`fas ${icon} text-sm mobile:text-[12px]/[1.4]`} style={{ color: accent }} />
      )}
    </div>
    <p
      className="mt-1.5 font-fourth text-2xl sm:text-xl mobile:text-[20px]/[1] font-bold leading-none"
      style={{ color: accent }}
    >
      {value}
    </p>
    {sub && <p className="mt-1 text-xs mobile:text-[11px]/[1.4] text-white/40">{sub}</p>}
  </div>
)

// A labelled horizontal bar. `max` is the value the bar is scaled against —
// pass the largest value in the group so a set of bars reads as one comparison.
export const StatBar: React.FC<{
  label: string
  value: string
  fraction: number
  color: string
  hint?: string
}> = ({ label, value, fraction, color, hint }) => (
  <div>
    <div className="flex items-baseline justify-between gap-3 mb-1">
      <span className="text-sm sm:text-xs mobile:text-[12px]/[1.4] text-white/70 truncate">{label}</span>
      <span className="text-sm sm:text-xs mobile:text-[12px]/[1.4] font-semibold shrink-0" style={{ color }}>
        {value}
      </span>
    </div>
    <div className="h-2 mobile:h-[7px] w-full overflow-hidden rounded-full bg-white/5">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{
          width: `${clampScore(fraction * 100)}%`,
          background: `linear-gradient(90deg, ${color}99, ${color})`,
        }}
      />
    </div>
    {hint && <p className="mt-1 text-[0.65rem] mobile:text-[10px]/[1.4] text-white/30">{hint}</p>}
  </div>
)

// A single label/number line, for the dense read-everything blocks.
export const StatRow: React.FC<{
  label: string
  value: string
  color?: string
}> = ({ label, value, color }) => (
  <div className="flex items-center justify-between gap-3 mobile:gap-[8px] border-b border-white/5 py-1.5 mobile:py-[5px] last:border-b-0">
    <span className="text-sm sm:text-xs mobile:text-[12px]/[1.4] text-white/50">{label}</span>
    <span
      className="text-sm sm:text-xs mobile:text-[12px]/[1.4] font-semibold"
      style={{ color: color || "#fff" }}
    >
      {value}
    </span>
  </div>
)

// Recharts tooltips default to a white card that is unreadable on this
// background, so every chart passes this one instead.
export const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-white/20 bg-black/90 px-3 py-2 shadow-xl">
      {label !== undefined && (
        <p className="mb-1 text-xs mobile:text-[11px]/[1.4] font-semibold text-white/70">{label}</p>
      )}
      {payload.map((entry: any, index: number) => (
        <p key={index} className="text-xs mobile:text-[11px]/[1.4]" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  )
}

export const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex h-32 mobile:h-[90px] items-center justify-center rounded-xl border border-dashed border-white/10 text-sm mobile:text-[12px]/[1.4] text-white/30">
    {text}
  </div>
)
